import { WorkSpaceWithBookingNode } from 'modules/space_control/hooks/useWorkSpacesWithBookings'

export const EVENT = {
  MAP_CLICK: 'map:click',
  SPACE_CLICK: 'space:click',
  SPACE_HOVER: 'space:hover',
  UPDATE_WORKSPACES: 'update:workspaces',
  ACTIVATE_MOVE_CURSOR: 'activate:move:cursor',
  RESET_CURSOR: 'reset:cursor',
} as const

export interface MapClickEvent {
  x: number
  y: number
}

export interface SpaceClickEvent {
  workSpaceId: number
}

export interface SpaceHoverEvent {
  workSpaceId: number | null
}

export interface UpdateWorkspacesEvent {
  workSpaces: WorkSpaceWithBookingNode[]
}

export interface ActivateMoveCursorEvent {}

export interface ResetCursorEvent {}

export interface MapEvents {
  [EVENT.MAP_CLICK]: MapClickEvent
  [EVENT.SPACE_CLICK]: SpaceClickEvent
  [EVENT.SPACE_HOVER]: SpaceHoverEvent
  [EVENT.UPDATE_WORKSPACES]: UpdateWorkspacesEvent
  [EVENT.ACTIVATE_MOVE_CURSOR]: ActivateMoveCursorEvent
  [EVENT.RESET_CURSOR]: ResetCursorEvent
}

type EventListener<T extends keyof MapEvents> = (data: MapEvents[T]) => void

export class MapEventBus {
  private listeners: {
    [K in keyof MapEvents]?: EventListener<K>[]
  } = {}

  emit<K extends keyof MapEvents>(event: K, data: MapEvents[K]): boolean {
    const eventListeners = this.listeners[event]
    if (!eventListeners) return false

    eventListeners.forEach((listener) => {
      try {
        listener(data)
      } catch {
        // Error handling is silent to prevent system crashes
      }
    })

    return true
  }

  on<K extends keyof MapEvents>(event: K, listener: EventListener<K>): this {
    this.listeners[event] ??= []
    const listeners = this.listeners[event]
    if (listeners) {
      listeners.push(listener)
    }
    return this
  }

  off<K extends keyof MapEvents>(event: K, listener: EventListener<K>): this {
    const eventListeners = this.listeners[event]
    if (!eventListeners) return this

    const index = eventListeners.indexOf(listener)
    if (index > -1) {
      eventListeners.splice(index, 1)
    }

    return this
  }

  removeAllListeners<K extends keyof MapEvents>(event?: K): this {
    if (event) {
      delete this.listeners[event]
    } else {
      this.listeners = {}
    }
    return this
  }

  clear(): void {
    this.listeners = {}
  }
}
